import { useEffect, useState } from 'react'
import { Check, CircleStop, Copy, FileText, Loader2, Play, Search, Sparkles, Table2, X } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { useApiFeatureFlag } from '@/hooks/useFeatureFlag'
import { Checkbox } from '@/components/ui/checkbox'
import { IntuiInput } from '@/components/intueri/IntuiInput'
import {
  IntuiSelect,
  IntuiSelectContent,
  IntuiSelectItem,
  IntuiSelectTrigger,
  IntuiSelectValue,
} from '@/components/intueri/IntuiSelect'
import { evidenceSearchedFiles } from '../evidenceSources'
import { toolDisplayLabel } from '../workflowGraph'
import { getWorkflowSettings, knowledgeFileName, subscribeWorkflowSettings, updateWorkflowSettings } from '../workflowSettings'
import { instructionProse, readableRows, specialLines, unwrapPayload } from '../readablePayload'
import type { WorkflowGraphNodeData } from '../workflowTypes'

const LABEL = 'text-sm font-medium text-foreground'

type Translate = ReturnType<typeof useTranslation>['t']

/** On-error policies (Figma "6. Node Properties" — On Error dropdown). */
const onErrorOptions = (t: Translate) =>
  [
    {
      value: 'stop',
      label: t('workflow:node.on_error_stop', { defaultValue: 'Stop workflow' }),
      description: t('workflow:node.on_error_stop_hint', {
        defaultValue: 'Halt execution and fail workflow',
      }),
    },
    {
      value: 'continue',
      label: t('workflow:node.on_error_continue', { defaultValue: 'Continue (using error output)' }),
      description: t('workflow:node.on_error_continue_hint', {
        defaultValue: 'Pass error message as item in regular output',
      }),
    },
    // 'error_path' ("Add error callback") is deliberately absent: the engine
    // has no configurable error paths yet (it remapped to 'stop'), and the
    // design narration flagged the option as droppable [spec §13.3]. Stored
    // 'error_path' values from older saves still validate server-side.
  ] as const

function stringify(value: unknown): string {
  if (value === undefined || value === null) return ''
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2)
}

/**
 * A step payload as the plain text the design's read-only box shows: the
 * model's own summary prose first (`reason` — the model already writes good
 * prose there), then claims/asks as sentences, then "Label: value" lines.
 */
function readableStepText(value: unknown): string {
  if (typeof value === 'string') return value
  const payload = unwrapPayload(value)
  const record =
    typeof payload === 'object' && payload !== null && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : {}
  const reason = typeof record.reason === 'string' ? record.reason : ''
  const summary = !reason && typeof record.summary === 'string' ? record.summary : ''
  // The unwrapped payload's own prose wins: step INPUTS have an instruction
  // but no reason/summary (instructionProse still renders them), while an
  // OUTPUT that happens to carry a stray instruction key must lead with the
  // model's result prose, not the prompt text.
  const prose = reason || summary || instructionProse(value)
  const special = specialLines(record)
  const rows = readableRows(payload).filter(
    (row) =>
      // Already rendered as prose/sentences above.
      !/^(Reason|Blocked Claims|Clarification Requests)/.test(row.label) &&
      !(summary && row.label === 'Summary')
  )
  return [prose, special.join('\n'), rows.map((row) => `${row.label}: ${row.text}`).join('\n')]
    .filter(Boolean)
    .join('\n\n')
}

/**
 * Human-readable section for a step payload (Input / Output / …), presented
 * the way Figma "6. Node Properties" draws it: a read-only textarea-style box
 * (an input you can't type into) holding plain text — selectable, scrollable,
 * resizable — plus a copy button that copies exactly that text. Only a payload
 * nothing readable can be extracted from falls back to raw JSON in the same box.
 */
function ReadableSection({
  title,
  value,
  placeholder,
  keepWhenEmpty,
}: {
  title: string
  value: unknown
  /** When the value is empty, render the box with this placeholder instead of
   *  hiding the section (Figma: Output shows "Workflow not started yet"). */
  placeholder?: string
  /**
   * Keep an empty box when the value is empty and there is no placeholder.
   * Output/Evidence: not run → placeholder; ran and empty → empty box. A draft
   * (no row) is "not run" and still gets the placeholder.
   */
  keepWhenEmpty?: boolean
}) {
  const [copied, setCopied] = useState(false)
  const raw = stringify(value)
  if (!raw) {
    if (!placeholder && !keepWhenEmpty) return null
    return (
      <div className="flex flex-col gap-2">
        <span className={LABEL}>{title}</span>
        <textarea
          readOnly
          value=""
          rows={7}
          {...(placeholder ? { placeholder } : {})}
          className="w-full resize-y rounded-lg border border-input bg-background p-3 font-mono text-xs leading-relaxed text-muted-foreground placeholder:text-muted-foreground focus:outline-none dark:bg-dark-card"
        />
      </div>
    )
  }
  const text = readableStepText(value) || raw
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className={LABEL}>{title}</span>
        <button
          type="button"
          aria-label={`Copy ${title.toLowerCase()}`}
          onClick={() => {
            void navigator.clipboard?.writeText(text).then(() => {
              setCopied(true)
              setTimeout(() => setCopied(false), 1500)
            })
          }}
          className="grid size-6 place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          {copied ? <Check className="size-3.5 text-green-600" /> : <Copy className="size-3.5" />}
        </button>
      </div>
      <textarea
        readOnly
        value={text}
        rows={7}
        className="w-full resize-y rounded-lg border border-input bg-background p-3 font-mono text-xs leading-relaxed text-muted-foreground focus:outline-none dark:bg-dark-card"
      />
    </div>
  )
}

/**
 * Evidence as consulted SOURCES ("3 sources: file.docx, Historian — TAG…"),
 * instead of a JSON array (or `workflow:…:tool:N` ids) squeezed into an
 * input box.
 */
// (evidenceSummary removed with the Evidence field \u2014 Putra 2026-07-15.)

/** Plain-language stand-in when the engine's error carries no readable message. */
const GENERIC_ERROR_SUMMARY = 'This step failed before it could produce a result.'

/**
 * Keep an error string END-USER readable (Putra "10. Update": "Error should
 * be summary not code"): refuse raw JSON blobs, and cut a stack trace down to
 * its message line(s).
 */
function humanizeErrorText(value: string): string | undefined {
  const trimmed = value.trim()
  if (!trimmed) return undefined
  // A serialized object/array is code, not a summary — hide it.
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) return undefined
  // "Error: boom\n    at fn (file.ts:1)" / Python tracebacks → keep the message.
  const stackIdx = trimmed.search(/\n\s+at\s|\nTraceback \(/)
  return stackIdx > 0 ? trimmed.slice(0, stackIdx).trim() : trimmed
}

/**
 * A failed step's error, split into the Figma "10. Update" shape:
 * What happened (the message) · Why (the reason) · What to do (remediation, or
 * omitted when the engine wrote none). Tolerant of a bare string / opaque
 * object — an unrecognizable shape gets a plain-language summary, NEVER raw
 * JSON (Putra: "Error should be summary not code for end user").
 */
function structuredError(
  error: unknown,
): { what: string; why?: string; whatToDo?: string } | null {
  if (error === null || error === undefined || error === '') return null
  if (typeof error === 'string') {
    // A pre-serialized JSON error ('{"message":"connection refused"}') still
    // carries a real message — parse and recurse rather than dropping to the
    // generic summary. Non-JSON strings fall through to plain humanizing.
    const trimmed = error.trim()
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed = structuredError(JSON.parse(trimmed))
        if (parsed) return parsed
      } catch {
        // Not valid JSON — treat as opaque code-ish text below.
      }
    }
    return { what: humanizeErrorText(error) ?? GENERIC_ERROR_SUMMARY }
  }
  if (typeof error !== 'object') return { what: String(error) }
  const e = error as Record<string, unknown>
  const str = (v: unknown): string | undefined =>
    typeof v === 'string' ? humanizeErrorText(v) : undefined
  const what = str(e.message) ?? str(e.what) ?? str(e.error) ?? GENERIC_ERROR_SUMMARY
  const why = str(e.reason) ?? str(e.why) ?? str(e.detail)
  const whatToDo = str(e.remediation) ?? str(e.whatToDo) ?? str(e.hint) ?? str(e.action)
  return { what, ...(why ? { why } : {}), ...(whatToDo ? { whatToDo } : {}) }
}

/**
 * Node properties inspector (Figma "6. Node Properties"), docked right of the
 * canvas. First nodes show OUTPUT only (a workflow's entry has no upstream
 * input); later nodes show Input + Output. Evidence, timestamps, attached
 * files, an editable on-error policy, and — for failed nodes — the error in a
 * resizable text area. The internal per-step `instruction` is deliberately
 * never rendered (backend-only).
 */
export default function NodePropertiesPanel({
  node,
  workflowId,
  isFirst,
  onClose,
  onRun,
  running = false,
  onStop,
  stopping = false,
}: {
  node: WorkflowGraphNodeData
  /** Settings key for persisting the on-error choice. */
  workflowId: string
  /** Entry node → no Input section. */
  isFirst: boolean
  onClose: () => void
  /** Footer "Run Workflow" (Figma pins it at the panel bottom). Hidden when absent. */
  onRun?: () => void
  running?: boolean
  /** Run in flight + cancellable: the footer becomes an actionable
   *  "Stop Workflow" (Figma "11. Stop", node 14757:29835). */
  onStop?: () => void
  /** The stop request is in flight → "Stopping…" spinner. */
  stopping?: boolean
}) {
  const { t } = useTranslation()
  const traceLinkFlag = useApiFeatureFlag('kg_trace_link_enabled', true)
  const step = node.step
  const phase = node.draftPhase
  // The `title:` prefix keeps a title-only fallback key from ever colliding
  // with a real step id — two same-titled nodes still share it, but ids/phase
  // ids (present for every generated node) win first.
  const stepKey = step?.stepId ?? phase?.id ?? `title:${node.title}`
  // 'error_path' was removed from the options (the engine always remapped it
  // to stop); alias stored legacy values so the dropdown never renders blank.
  const storedOnError = (value: string | undefined) =>
    value === 'error_path' ? 'stop' : (value ?? 'stop')
  const [onError, setOnError] = useState(() =>
    storedOnError(getWorkflowSettings(workflowId).onErrorByStep[stepKey])
  )
  // The panel stays mounted while the selected node changes — re-read the
  // persisted policy whenever the key changes instead of keeping the first
  // node's value.
  useEffect(() => {
    setOnError(storedOnError(getWorkflowSettings(workflowId).onErrorByStep[stepKey]))
  }, [workflowId, stepKey])
  // Per-step HITL control (Figma §9): persisted override wins, else the
  // node's plan default. false = the step runs unattended (never pauses).
  const [hitlOn, setHitlOn] = useState(
    () => getWorkflowSettings(workflowId).hitlByStep[stepKey] ?? node.hitl === true
  )
  useEffect(() => {
    setHitlOn(getWorkflowSettings(workflowId).hitlByStep[stepKey] ?? node.hitl === true)
    // node.hitl tracks stepKey; re-reading on key change is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, stepKey])
  const errorStruct = structuredError(step?.error)
  // Files react to settings writes (Select-files dialog attaches while this
  // panel is open), not just to mount time. Display names only — entries may
  // carry documentIds for run scoping.
  const readFileNames = () => getWorkflowSettings(workflowId).knowledgeFiles.map(knowledgeFileName)
  const [files, setFiles] = useState<string[]>(readFileNames)
  useEffect(() => {
    setFiles(readFileNames())
    return subscribeWorkflowSettings((changedId) => {
      if (changedId === workflowId) setFiles(readFileNames())
    })
    // readFileNames is derived from workflowId (stable per render pass).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId])
  // Files this node actually SEARCHED (doc names out of its evidence).
  const searchedFiles = evidenceSearchedFiles(step?.evidence)
  // Row is opened before the work, so startedAt is set for anything that ran — including a failure.
  const hasRun = Boolean(step?.startedAt)
  const notStarted = t('workflow:node.not_started', { defaultValue: 'Workflow not started yet' })

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-4 py-3.5">
        <div className="flex min-w-0 items-center gap-2">
          <Sparkles className="size-4 shrink-0 text-foreground" />
          <h2 className="min-w-0 truncate text-sm font-semibold text-foreground" title={node.title}>{node.title}</h2>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {/* Per-step forensics deep-link — the workflow twin of the chat
              magnifying glass (AGNT-685). Renders only when the step run
              carries a correlation id (the engine stamps one for agentic /
              verify / fanout steps) AND the kg_trace_link_enabled flag is on.
              New tab so the builder state is preserved. Engineer-facing — no
              i18n, matching the chat link. */}
          {traceLinkFlag.enabled && step?.correlationId && (
            <a
              href={`/kg-trace/${encodeURIComponent(step.correlationId)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="workflow-step-trace-link"
              className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              title="Open agent trace (engineer debug)"
              aria-label="Open agent trace (engineer debug)"
            >
              <Search className="size-4" aria-hidden="true" />
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common:close', { defaultValue: 'Close' })}
            className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overflow-x-hidden p-4 [&>*]:shrink-0">
        {/* On error policy */}
        <div className="flex flex-col gap-2">
          <span className={LABEL}>{t('workflow:node.on_error', { defaultValue: 'On error' })}</span>
          <IntuiSelect
            value={onError}
            onValueChange={(value) => {
              setOnError(value)
              const settings = getWorkflowSettings(workflowId)
              updateWorkflowSettings(workflowId, {
                onErrorByStep: { ...settings.onErrorByStep, [stepKey]: value },
              })
            }}
          >
            <IntuiSelectTrigger className="h-auto min-h-9 py-2 [&>span]:line-clamp-none">
              <IntuiSelectValue />
            </IntuiSelectTrigger>
            <IntuiSelectContent>
              {onErrorOptions(t).map((option) => (
                <IntuiSelectItem key={option.value} value={option.value}>
                  <span className="flex flex-col text-left">
                    <span>{option.label}</span>
                    <span className="text-xs text-muted-foreground">{option.description}</span>
                  </span>
                </IntuiSelectItem>
              ))}
            </IntuiSelectContent>
          </IntuiSelect>
        </div>

        {/* Editable on run views too: the toggle writes hitlByStep, which the
            engine re-reads LIVE at each step boundary — so ticking during a
            run gates that run's REMAINING steps (already-executed steps are
            history). Checked = the run PARKS after this step for human
            review; unchecked = runs through (agent-initiated asks also
            suppressed). */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Checkbox
              checked={hitlOn}
              disabled={node.hitlReadonly === true}
              onCheckedChange={(value) => {
                if (node.hitlReadonly) return
                const next = value === true
                setHitlOn(next)
                const current = getWorkflowSettings(workflowId)
                updateWorkflowSettings(workflowId, {
                  hitlByStep: { ...current.hitlByStep, [stepKey]: next },
                })
              }}
            />
            {t('workflow:builder.hitl', { defaultValue: 'Human in the loop' })}
          </label>
          <p className="mt-1 pl-6 text-xs text-muted-foreground">
            {node.hitlReadonly
              ? t('workflow:builder.hitl_hint_v2', {
                  defaultValue:
                    'Reports that this step can ask when the plant data cannot answer. The engine does not read this override.',
                })
              : t('workflow:builder.hitl_hint', {
                  defaultValue:
                    'Pauses the run after this step so you can review its output and steer the next steps. Takes effect immediately, including the current run.',
                })}
          </p>
        </div>

        {/* Failed node: structured error (Figma "10. Update") — What happened /
            Why / What to do, from whatever the engine wrote onto step.error. */}
        {errorStruct && (
          <div className="flex flex-col gap-2">
            <span className={LABEL}>{t('workflow:node.error', { defaultValue: 'Error' })}</span>
            <div className="flex flex-col gap-2 rounded-lg border border-red-300 bg-red-50/50 p-3 text-xs leading-relaxed text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
              <div>
                <p className="font-semibold">
                  {t('workflow:node.error_what', { defaultValue: 'What happened' })}
                </p>
                <p>{errorStruct.what}</p>
              </div>
              {errorStruct.why && (
                <div>
                  <p className="font-semibold">
                    {t('workflow:node.error_why', { defaultValue: 'Why' })}
                  </p>
                  <p>{errorStruct.why}</p>
                </div>
              )}
              {errorStruct.whatToDo && (
                <div>
                  <p className="font-semibold">
                    {t('workflow:node.error_todo', { defaultValue: 'What to do' })}
                  </p>
                  <p>{errorStruct.whatToDo}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Input only exists past the entry node — the first node starts the workflow. */}
        {!isFirst && (
          <ReadableSection title={t('workflow:node.input', { defaultValue: 'Input' })} value={step?.input} />
        )}
        {/* Timestamps — placeholder until the workflow has actually run
            (Figma / Putra 2026-07-15: "Workflow not started yet"). */}
        <div className="flex flex-col gap-2">
          <span className={LABEL}>{t('workflow:node.started_at', { defaultValue: 'Started at' })}</span>
          <IntuiInput
            readOnly
            value={step?.startedAt ?? ''}
            placeholder={notStarted}
          />
        </div>
        <div className="flex flex-col gap-2">
          <span className={LABEL}>{t('workflow:node.completed_at', { defaultValue: 'Completed at' })}</span>
          <IntuiInput
            readOnly
            value={step?.completedAt ?? ''}
            placeholder={notStarted}
          />
        </div>

        <ReadableSection
          title={t('workflow:node.output', { defaultValue: 'Output' })}
          value={step?.output}
          placeholder={hasRun ? undefined : notStarted}
          keepWhenEmpty={hasRun}
        />

        {/* Evidence (Figma "10. Update" re-adds it): the evidence ledger this
            step produced, humanized. Like Input, only past the entry node. */}
        {!isFirst && (
          <ReadableSection
            title={t('workflow:node.evidence', { defaultValue: 'Evidence' })}
            value={step?.evidence}
            placeholder={hasRun ? undefined : notStarted}
            keepWhenEmpty={hasRun}
          />
        )}

        {/* Draft-only detail: what the planned step will do. */}
        {!step && phase && (
          <>
            <ReadableSection
              title={t('workflow:node.completion', { defaultValue: 'Completion criteria' })}
              value={phase.completionCriteria}
            />
            <ReadableSection
              title={t('workflow:node.tools', { defaultValue: 'Likely tools' })}
              value={phase.likelyTools.map(toolDisplayLabel).join(', ') || 'No direct tools'}
            />
          </>
        )}

        {/* Files this node actually SEARCHED (from its evidence) — distinct
            from the workflow-level attachments below. */}
        {searchedFiles.length > 0 && (
          <div className="flex flex-col gap-2">
            <span className={LABEL}>
              {t('workflow:node.files_searched', { defaultValue: 'Files searched' })}
            </span>
            <ul className="flex flex-wrap gap-1.5">
              {searchedFiles.map((file) => (
                <li
                  key={file}
                  title={file}
                  className="flex max-w-full items-center gap-1.5 rounded-md border border-border bg-muted/30 px-2 py-1 text-xs text-foreground"
                >
                  <FileText className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 truncate">{file}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Source documents attached to this workflow (Figma / Putra: "Source"). */}
        {files.length > 0 && (
          <div className="flex flex-col gap-2">
            <span className={LABEL}>{t('workflow:node.source', { defaultValue: 'Source' })}</span>
            <ul className="flex flex-col gap-1.5">
              {files.map((file) => (
                <li
                  key={file}
                  className="flex min-w-0 items-center gap-2 overflow-hidden rounded-lg border border-border px-3 py-2 text-sm text-foreground"
                >
                  {/\.(csv|xlsx?)$/i.test(file) ? (
                    <Table2 className="size-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <FileText className="size-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className="min-w-0 flex-1 truncate" title={file}>{file}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Footer: Run Workflow — orange with a play icon (Putra 2026-07-15).
          While the run is in flight it becomes an actionable "Stop Workflow"
          (Figma "11. Stop", node 14757:29835) when cancellable (onStop); the
          passive disabled "Running…" spinner remains the fallback for the
          startup blip before the run row exists. Stays rendered through the
          run (running) even after onRun clears, so the button doesn't vanish
          the moment the run starts. */}
      {(onRun || running) && (
        <div className="flex shrink-0 justify-end border-t border-border p-4">
          {running && onStop ? (
            <button
              type="button"
              onClick={onStop}
              disabled={stopping}
              // The third surface for the same `requestStop`. Putra's "both" (Slack
              // 2026-09-10) is this one and the node pill -- the two that were orange.
              // Left orange it would simply move the inconsistency into the panel.
              className="inline-flex items-center gap-1.5 rounded-lg bg-foreground px-3.5 py-2 text-sm font-medium text-background shadow-sm transition-colors hover:bg-foreground/90 disabled:opacity-70"
            >
              {stopping ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  {t('workflow:builder.stopping', { defaultValue: 'Stopping…' })}
                </>
              ) : (
                <>
                  <CircleStop className="size-3.5" aria-hidden />
                  {t('workflow:builder.stop', { defaultValue: 'Stop Workflow' })}
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={onRun}
              disabled={running || !onRun}
              // The third surface for the same `requestStop`. Putra's "both" (Slack
              // 2026-09-10) is this one and the node pill -- the two that were orange.
              // Left orange it would simply move the inconsistency into the panel.
              className="inline-flex items-center gap-1.5 rounded-lg bg-foreground px-3.5 py-2 text-sm font-medium text-background shadow-sm transition-colors hover:bg-foreground/90 disabled:opacity-70"
            >
              {running ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  {t('workflow:builder.running', { defaultValue: 'Running…' })}
                </>
              ) : (
                <>
                  <Play className="size-3.5 fill-current" aria-hidden />
                  {t('workflow:builder.run', { defaultValue: 'Run Workflow' })}
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
